import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { ButtonModule, CheckboxModule, InputModule, RadioModule, SelectModule, TextareaModule } from '@sag-ui';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ProductDamageInternationalForm, ProductDamageInternationalFormGroup } from '../../../models/cod-create.model';
import { AttachmentsInputComponent } from '../../../../shared/components/attachments-input/attachments-input.component';
import { DamageIssueType, ItemCondition, PackageType, PackageTypeCondition } from '@cod-monorepo/interfaces';
import { filter, tap } from 'rxjs';
import { Store } from '@ngxs/store';
import { AddCurrentCodLine, AddErrorMessage, RemoveErrorMessage } from '../../../state/cod.action';
import { ItemConditionMapping } from '../../../../shared/mappings/item-condition.mapping';
import { WorkflowError } from '../../../../shared/enums/workflow-error';
import { BaseInternationalStep } from '../base-international-step/base-international-step';

@Component({
    selector: 'app-cod-product-damage-international-step',
    templateUrl: './product-damage-international.component.html',
    imports: [
        CommonModule,
        SelectModule,
        InputModule,
        FormsModule,
        ReactiveFormsModule,
        RadioModule,
        CheckboxModule,
        ButtonModule,
        TextareaModule,
        AttachmentsInputComponent
    ],
    standalone: true
})
export class CodProductDamageInternationalStepComponent extends BaseInternationalStep implements OnInit {
    @Input() productDamageInternationalForm: FormGroup<ProductDamageInternationalFormGroup> | undefined;

    public damageTypes = DamageIssueType;
    public packageTypes = PackageType;
    public packageTypeConditions = PackageTypeCondition;
    public itemConditions = ItemCondition;
    public itemConditionMapping = ItemConditionMapping;

    public showFinancialAdjustment = false;

    constructor(private store: Store) {
        super();
    }

    ngOnInit(): void {
        this.initChangeListener();
        this.initDamageClaimedChangeListener();
    }

    public isDefined(control: keyof ProductDamageInternationalForm): boolean {
        return this.productDamageInternationalForm?.controls[control].value !== null;
    }

    public isTrue(control: keyof ProductDamageInternationalForm): boolean {
        return this.productDamageInternationalForm?.controls[control].value === true;
    }

    public isFalse(control: keyof ProductDamageInternationalForm): boolean {
        return this.productDamageInternationalForm?.controls[control].value === false;
    }

    public itemConditionSelected(condition: ItemCondition): boolean {
        return this.productDamageInternationalForm?.controls.itemCondition.value?.includes(condition) ?? false;
    }

    public toggleItemCondition(condition: ItemCondition): void {
        const itemCondition = this.productDamageInternationalForm?.controls.itemCondition;

        if (itemCondition) {
            const conditions = itemCondition.value ?? [];

            if (conditions.includes(condition)) {
                itemCondition.setValue(conditions.filter(c => c !== condition));
            } else {
                itemCondition.setValue([...conditions, condition]);
            }
        }
    }

    public evaluate(): void {
        const visualEvidence = this.productDamageInternationalForm?.controls.visualEvidence.value ?? [];
        const damageType = this.productDamageInternationalForm?.controls.damageType.value;

        if (damageType === DamageIssueType.APPARENT_OUTSIDE_PACKAGING) {
            this.showFinancialAdjustment = visualEvidence.length > 0;
            return;
        }

        this.showFinancialAdjustment = this.isDefined('canIdentifyCartonId');
    }

    public handleConfirm() {
        if (this.productDamageInternationalForm?.valid) {
            this.store.dispatch(new AddCurrentCodLine());
        }
    }

    public isValid(): boolean {
        return this.productDamageInternationalForm?.valid ?? false;
    }

    private initChangeListener(): void {
        this.productDamageInternationalForm?.valueChanges.pipe(tap(() => this.evaluate())).subscribe();
    }

    private initDamageClaimedChangeListener(): void {
        this.productDamageInternationalForm?.controls.damageClaimFiled.valueChanges
            .pipe(
                filter(value => value !== null),
                tap(value =>
                    this.store.dispatch(
                        value
                            ? new RemoveErrorMessage(WorkflowError.FILE_CARRIER_CLAIM)
                            : new AddErrorMessage(WorkflowError.FILE_CARRIER_CLAIM)
                    )
                )
            )
            .subscribe();
    }
}
